// src/components/ArticleDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Heart, Calendar, Eye, Share2 } from 'lucide-react';

import { getArticle, likeArticle, getComments } from '../utils/api';
import { createWebSocket } from '../utils/websocket';
import AnimatedSocialLinks from './AnimatedSocialLinks';
import CommentForm from './CommentForm';
import CommentList from './CommentList';

import {
  TwitterShareButton,
  LinkedinShareButton,
  FacebookShareButton,
  WhatsappShareButton,
} from 'react-share';

import axios from 'axios';

const ArticleDetail = ({ user }) => {
  const { id: articleId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [liked, setLiked] = useState(false);
  const [error, setError] = useState(null);

  // Fetch initial like status
  const fetchLikeStatus = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/articles/${articleId}/like_status/`,
        { withCredentials: true }
      );
      setLiked(res.data.liked);
    } catch (err) {
      console.error('Failed to fetch like status');
    }
  };

  useEffect(() => {
    fetchLikeStatus();
  }, [articleId]);

  // CSRF token
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, {
      withCredentials: true,
    }).catch(() => {});
  }, []);

  const { data: article, isLoading, error: queryError } = useQuery({
    queryKey: ['article', articleId],
    queryFn: () => getArticle(articleId).then((res) => res.data),
  });

  const { data: comments = [], refetch: refetchComments } = useQuery({
    queryKey: ['comments', articleId],
    queryFn: () => getComments(articleId).then((res) => res.data),
    enabled: !!article,
  });

  const likeMutation = useMutation({
    mutationFn: () => likeArticle(articleId),
    onSuccess: (data) => {
      queryClient.setQueryData(['article', articleId], (old) => ({
        ...old,
        likes_count: data.likes_count,
      }));
      fetchLikeStatus();
      setError(null);
    },
    onError: (err) => {
      let message = 'Failed to like article.';
      if (err.response?.data?.error) message = err.response.data.error;
      setError(message);
      fetchLikeStatus();
    },
  });

  const shareMutation = useMutation({
    mutationFn: (platform) =>
      axios.post(
        `${process.env.REACT_APP_API_URL}/api/articles/${articleId}/share/`,
        { platform },
        { withCredentials: true }
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['article', articleId], (old) => ({
        ...old,
        shares_count: data.data.shares_count,
      }));
    },
  });

  useEffect(() => {
    if (queryError?.response?.status === 404) {
      navigate('/blog', { replace: true });
    }
  }, [queryError, navigate]);

  // WebSocket for real-time comments
  useEffect(() => {
    if (article) {
      const ws = createWebSocket(articleId, (data) => {
        if (data.comment) {
          queryClient.setQueryData(['comments', articleId], (old) => [
            ...(old || []),
            data.comment,
          ]);
        }
      });
      return () => ws.close();
    }
  }, [article, articleId, queryClient]);

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article) return null;

  const articleUrl = `https://serrand.org/blog/${article.id}`;
  const shareContent = `${article.title}\n${article.excerpt}\n${articleUrl}`;

  return (
    <div className="relative z-10">
      <div className="container max-w-5xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Main Article Content */}
          <div className="lg:col-span-8">
            <Card className="border-none shadow-none">
              <CardContent className="p-0">
                <article>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-6">
                    {article.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground mb-8">
                    <div>By <span className="font-medium text-foreground">{article.author_username}</span></div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(article.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="flex items-center gap-5">
                      <span className="flex items-center gap-1"><Eye className="h-4 w-4" /> {article.view_count || 0}</span>
                      <span className="flex items-center gap-1"><Heart className="h-4 w-4" /> {article.likes_count || 0}</span>
                      <span className="flex items-center gap-1"><Share2 className="h-4 w-4" /> {article.shares_count || 0}</span>
                    </div>
                  </div>

                  {article.thumbnail && (
                    <div className="mb-10 rounded-xl overflow-hidden">
                      <img
                        src={article.thumbnail}
                        alt={article.title}
                        className="w-full h-auto object-cover"
                      />
                    </div>
                  )}

                  {error && (
                    <Alert variant="destructive" className="mb-8">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div
                    className="prose prose-neutral dark:prose-invert max-w-none text-foreground prose-headings:font-semibold prose-a:text-primary"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />

                  {/* Like & Share Buttons */}
                  <div className="flex items-center gap-4 mt-12 pt-8 border-t">
                    <Button
                      variant={liked ? "default" : "outline"}
                      size="lg"
                      onClick={() => likeMutation.mutate()}
                      disabled={likeMutation.isPending}
                      className="flex items-center gap-2"
                    >
                      <Heart className={`h-5 w-5 ${liked ? 'fill-current text-red-500' : ''}`} />
                      Like
                    </Button>

                    <div className="ml-auto flex gap-2">
                      <TwitterShareButton url={articleUrl} title={shareContent} onShareWindowClose={() => shareMutation.mutate('twitter')}>
                        <Button variant="outline" size="icon">𝕏</Button>
                      </TwitterShareButton>
                      <LinkedinShareButton url={articleUrl} title={article.title} summary={article.excerpt} onShareWindowClose={() => shareMutation.mutate('linkedin')}>
                        <Button variant="outline" size="icon">in</Button>
                      </LinkedinShareButton>
                      <FacebookShareButton url={articleUrl} quote={shareContent} onShareWindowClose={() => shareMutation.mutate('facebook')}>
                        <Button variant="outline" size="icon">f</Button>
                      </FacebookShareButton>
                      <WhatsappShareButton url={articleUrl} title={shareContent} onShareWindowClose={() => shareMutation.mutate('whatsapp')}>
                        <Button variant="outline" size="icon">💬</Button>
                      </WhatsappShareButton>
                    </div>
                  </div>
                </article>

                <Separator className="my-12" />

                {/* Comments Section */}
                {user ? (
                  <>
                    <CommentForm articleId={articleId} refetchComments={refetchComments} />
                    <CommentList comments={comments} />
                  </>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-muted-foreground">Please log in to leave a comment.</p>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-24">
              <Card>
                <CardHeader>
                  <CardTitle>Get In Touch</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-6">
                    Have questions or feedback about this article?
                  </p>
                  <AnimatedSocialLinks className="flex flex-col gap-3" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;
